// V2 port of v1's MaterialRequirementsTable.
//
// Renders per-component requirements for a v2 work order. Mirrors v1 column
// layout (Material, Per Unit, Total Needed, Available, Shortage, Status)
// pixel-for-pixel, but pulls data from
// `/api/v2/work-orders/{id}/material-requirements/?step=` instead of v1's
// `/api/work-orders/{id}/material_requirements/`.

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, CircularProgress,
} from '@mui/material';
import { Inventory, Block, CheckCircle } from '@mui/icons-material';
import { v2 } from '../../../../services/api';

// Round to 3dp and drop trailing zeros. Float maths like 0.33 × 22 =
// 7.260000000000001 (and shortage epsilons like -8.88e-16) must never
// reach the operator — or flip a row to SHORT over nothing.
const round3 = (v) => Math.round((parseFloat(v) || 0) * 1000) / 1000;
const fmt3 = (v) => String(round3(v));

export default function MaterialRequirementsTableV2({
  workOrderId, step, remainingOverride, workOrderIds,
}) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Consolidated groups span multiple WOs (one explosion tree per order
  // line). Sending them all lets the backend sum availability across the
  // group's trees instead of scoping to workOrderId's tree alone — group
  // demand vs single-tree supply reads as a false SHORT. Joined to a string
  // so the effect dep is stable across re-renders.
  const workOrdersKey = (workOrderIds || []).join(',');

  // remaining = qty_to_produce - quantity_completed (for the step that the
  // requirement is scoped to, or the WO as a whole if no step provided).
  // remainingOverride lets a consolidated group scale the per-unit figures
  // to the WHOLE group's remaining quantity (the per-unit requirements are
  // identical across the group — same item, same operation).
  const remaining = remainingOverride != null
    ? Math.max(0, parseFloat(remainingOverride) || 0)
    : step
      ? Math.max(0, parseFloat(step.quantity_to_produce || 0) - parseFloat(step.quantity_completed || 0))
      : 0;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = step?.step_id ? { step: step.step_id } : {};
        if (workOrdersKey) params.work_orders = workOrdersKey;
        const res = await v2.workOrders.materialRequirements(workOrderId, params);
        if (cancelled) return;
        setRequirements(res.data?.requirements || []);
      } catch (err) {
        console.error('MaterialRequirementsTableV2: failed', err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (workOrderId) load();
    return () => { cancelled = true; };
  }, [workOrderId, step?.step_id, workOrdersKey]);

  if (loading) return <CircularProgress size={20} />;
  if (error) {
    return (
      <Typography variant="body2" color="error.main">
        Failed to load material requirements.
      </Typography>
    );
  }
  if (requirements.length === 0) {
    return (
      <Typography variant="body2">
        No material requirements for this step
      </Typography>
    );
  }

  return (
    <Box data-testid="material-requirements-table-v2">
      <Typography
        variant="subtitle2"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Inventory />
        Material Requirements
        {step?.total_steps > 1 && (
          <Chip
            label={`For Step ${step.sequence}/${step.total_steps}`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
        <Chip
          label={`${remaining} units remaining`}
          size="small"
          variant="outlined"
          color="primary"
        />
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Material</TableCell>
            <TableCell align="right">Per Unit</TableCell>
            <TableCell align="right">Total Needed</TableCell>
            <TableCell align="right">Available</TableCell>
            <TableCell align="right">Shortage</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requirements.map((req) => {
            const totalNeeded = round3((parseFloat(req.required) || 0) * remaining);
            const available = round3(req.available);
            const shortage = Math.max(0, round3(totalNeeded - available));
            const isShort = shortage > 0;
            return (
              <TableRow
                key={req.item_id}
                data-testid={`material-req-row-${req.item_id}`}
                sx={{
                  backgroundColor: isShort ? '#fff3e0' : '#e8f5e9',
                  '& td': {
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {req.item_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {req.item_code}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {fmt3(req.required)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={isShort ? 'error.main' : 'inherit'}
                  >
                    {fmt3(totalNeeded)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const onHand = req.on_hand != null ? parseFloat(req.on_hand) : null;
                    const reservedByOthers = req.v2_reserved_by_others != null
                      ? parseFloat(req.v2_reserved_by_others) : null;
                    const v1Reserved = req.v1_reserved != null
                      ? parseFloat(req.v1_reserved) : null;
                    // Issued to this step already, waiting to be used.
                    const inHand = req.in_hand != null ? parseFloat(req.in_hand) : 0;
                    // Show the net number that the v2 system uses for
                    // availability decisions; clamp display at 0 to avoid the
                    // confusing "-10 available" reading (a negative just means
                    // more is reserved than on-hand). The breakdown below
                    // explains the math.
                    const netDisplay = Math.max(0, available);
                    return (
                      <>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={available >= totalNeeded ? 'success.main' : 'error.main'}
                        >
                          {fmt3(netDisplay)}
                        </Typography>
                        {onHand != null && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', lineHeight: 1.2 }}
                          >
                            {fmt3(onHand)} on hand
                            {reservedByOthers > 0
                              ? ` · ${fmt3(reservedByOthers)} reserved by other v2 WOs`
                              : ''}
                            {v1Reserved > 0
                              ? ` · ${fmt3(v1Reserved)} reserved by v1`
                              : ''}
                            {inHand > 0
                              ? ` · ${fmt3(inHand)} already issued to this WO`
                              : ''}
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </TableCell>
                <TableCell align="right">
                  {shortage > 0 ? (
                    <Typography variant="body2" color="error.main" fontWeight="bold">
                      -{fmt3(shortage)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={isShort ? 'SHORT' : 'READY'}
                    size="small"
                    color={isShort ? 'error' : 'success'}
                    icon={isShort ? <Block /> : <CheckCircle />}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
